/** Filters out Go zero dates (0001-01-01) and falsy values. */
export function isValidDate(val: unknown): val is string {
  if (!val || typeof val !== "string") return false;
  return !val.startsWith("0001-01-01");
}

/** The house date rendering: abbreviated month, in whatever locale is reading. */
const SHORT_DATE: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "numeric" };

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
/** A full calendar date followed by a time — the only partial-date shape that may be parsed. */
const TIMESTAMP = /^\d{4}-\d{2}-\d{2}[T ]/;

/**
 * Parse a value for display, or null when it is not a date this app renders.
 *
 * A date-only string (`YYYY-MM-DD`, the shape of `due_date` /
 * `next_test_due_date`) is built as a LOCAL calendar date so it never shifts a
 * day: `new Date("2026-04-15")` parses as UTC midnight and renders as the 14th
 * in every negative-offset zone. A timestamp keeps the timezone-aware parse,
 * which is what makes its offset handling correct. Everything else — including
 * a half-typed `2026-09`, which `Date` would happily read as a UTC month
 * boundary and render as the last day of August — is refused rather than
 * guessed at.
 */
function parseDisplayDate(value: string): Date | null {
  const dateOnly = DATE_ONLY.exec(value);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  if (!TIMESTAMP.test(value)) return null;
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * THE date renderer for this app: the calendar date alone, in the reader's own
 * locale and zone. Accepts a date-only string or a timestamp — a timestamp
 * renders the date it fell on, which is what "date only" names.
 *
 * One helper, deliberately. Two renderers with different locale behaviour once
 * split the same `due_date` across a card/table toggle, and a hardcoded locale
 * printed English months at a Spanish-reading tester on a product that ships
 * complete Spanish copy. Optional Intl options override the default rendering;
 * an unparseable value comes back verbatim rather than as `Invalid Date`.
 */
export function formatDateOnly(dateStr: string, options?: Intl.DateTimeFormatOptions): string {
  const parsed = parseDisplayDate(dateStr);
  if (parsed === null) return dateStr;
  return parsed.toLocaleDateString(undefined, options ?? SHORT_DATE);
}

/**
 * Returns the best available date for a sampling event.
 * Schedule-generated events set occurrence_date; manually-created events set
 * scheduled_date. Falls back through the chain, skipping Go zero dates.
 */
export function effectiveEventDate(event: {
  occurrence_date?: string | null;
  scheduled_date?: string | null;
  collected_date?: string | null;
  created_at?: string;
}): string | null {
  if (isValidDate(event.occurrence_date)) return event.occurrence_date;
  if (isValidDate(event.scheduled_date)) return event.scheduled_date;
  if (isValidDate(event.collected_date)) return event.collected_date;
  if (event.created_at) return event.created_at.split("T")[0] ?? null;
  return null;
}

/** Formats the effective event date for display, returning "-" if no valid date. */
export function formatEventDate(event: {
  occurrence_date?: string | null;
  scheduled_date?: string | null;
  collected_date?: string | null;
  created_at?: string;
}): string {
  const d = effectiveEventDate(event);
  return d ? formatDateOnly(d) : "-";
}

/** Checks if a date string represents a date before today (date-only, no time component). */
export function isExpiredDate(val: string): boolean {
  const date = new Date(val);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
}

/** `SHORT_DATE` plus the clock, so a timestamp reads like the dates beside it. */
const SHORT_DATE_TIME: Intl.DateTimeFormatOptions = {
  ...SHORT_DATE,
  hour: "2-digit",
  minute: "2-digit",
};

/**
 * An ISO 8601 timestamp (e.g. `2026-04-22T14:05:12Z`) as a localized date +
 * time in the viewer's own zone — the same rendering `formatDateOnly` gives,
 * with the clock. An empty value or a Go zero time reads as the empty glyph
 * rather than as a date nobody meant; anything else unparseable comes back
 * verbatim.
 */
export function formatDateTime(val: string | null | undefined): string {
  if (!val) return "-";
  if (!isValidDate(val)) return "-";
  const parsed = parseDisplayDate(val);
  if (parsed === null) return val;
  return parsed.toLocaleString(undefined, SHORT_DATE_TIME);
}

/**
 * A submission's performed value as the record prints it. `performedDate`
 * (`performed_date`, `YYYY-MM-DD`) is the calendar day the performer named
 * when the answer was a date: it is the day, printed as a date with no time
 * and never read through a zone. Null — or a row that carries no day — means
 * the answer was an instant, printed from `iso` with its time.
 */
export function formatPerformedAt(iso: string, performedDate: string | null): string {
  return performedDate ? formatDateOnly(performedDate) : formatDateTime(iso);
}

/** An instant's time of day ("4:44 PM") in the given UI language and the viewer's own zone. */
export function formatTimeOfDay(iso: string, language: string): string {
  return new Intl.DateTimeFormat(language, { hour: "numeric", minute: "2-digit" }).format(
    new Date(iso),
  );
}

/**
 * `YYYY-MM-DD` for a Date read in the LOCAL frame — never `toISOString`, which
 * shifts the day west of UTC and would date a Home window a day early for
 * every reader in the Americas.
 */
export function localDateString(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export interface MonthRange {
  /** Inclusive first calendar day of the month, `YYYY-MM-DD` in the reader's own zone. */
  startDate: string;
  /** Inclusive last calendar day, same frame. */
  endDate: string;
  /**
   * The opening boundary as an RFC3339 instant — `performed_from`'s wire shape,
   * which is a date-TIME. Local midnight rather than UTC midnight, so a test
   * performed at 8pm on the last day of the previous month west of UTC does not
   * count toward this one.
   */
  performedFrom: string;
  /**
   * The closing boundary, drawn on the LAST MILLISECOND of `endDate` in the same
   * frame — `performed_to` is inclusive, so midnight would drop everything
   * performed on the closing day.
   *
   * Both edges are sent, always. `performed_at` is a tester-filled answer with no
   * future-date guard anywhere in the stack, so an open-ended count silently
   * absorbs a mistyped year or a skewed offline clock into a window whose label
   * names two dates.
   */
  performedTo: string;
}

/**
 * The calendar month `now` falls in, as both calendar dates and wire instants.
 *
 * `now` is a PARAMETER rather than a wall-clock read: every boundary here is a
 * date boundary, and a function that reads the clock itself is flaky at every
 * month end and untestable at the boundaries it exists to draw. Day-zero of the
 * next month is Date's own overflow rule for the last day of this one, which is
 * what makes February — leap year or not — need no special case.
 */
export function currentMonthRange(now: Date): MonthRange {
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const endOfDay = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999);

  return {
    startDate: localDateString(start),
    endDate: localDateString(end),
    performedFrom: start.toISOString(),
    performedTo: endOfDay.toISOString(),
  };
}

/**
 * The inclusive `due_to` bound `days` ahead of `now`, `YYYY-MM-DD` — `due_to`'s
 * wire shape is a date, not an instant. Clock-injected for the reason above, and
 * month/year rollover is Date's own arithmetic rather than a branch here.
 */
export function dueCutoff(now: Date, days: number): string {
  return localDateString(new Date(now.getFullYear(), now.getMonth(), now.getDate() + days));
}

/**
 * Add 12 calendar months to a YYYY-MM-DD date, returning YYYY-MM-DD. The TCEQ
 * 12-month calibration-currency default, shared by the company fleet registry
 * and the tester's own test kits. Date-only math avoids the timezone day-shift
 * `new Date("YYYY-MM-DD")` introduces.
 */
export function addTwelveMonths(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  if (!y || !m || !d) return "";
  const base = new Date(y, m - 1, d);
  base.setFullYear(base.getFullYear() + 1);
  const yy = base.getFullYear();
  const mm = String(base.getMonth() + 1).padStart(2, "0");
  const dd = String(base.getDate()).padStart(2, "0");
  return `${String(yy)}-${mm}-${dd}`;
}
